/**
 * Per-tab shipment totals for the toolbar's "N Shipments" — everything a tab's
 * filter matches, across all of its pages, for whichever tab is on screen.
 *
 * The count used to be a single number every tab's table wrote into. Tabs that are
 * kept alive — the shipment list, search results, overview widgets — go on fetching
 * behind the one on screen, so the last table to answer won, and coming back to a
 * kept-alive tab showed some other tab's count because that tab had nothing to
 * refetch. Keeping a count per tab means the toolbar can show the one belonging to
 * the tab actually in front of the user.
 *
 * The setters have to be stable per tab: each table lists its `setTotalShipments`
 * in the dependencies of the callback that fetches, so handing out a new function
 * on every render would refetch the grid on every render.
 */
import { useCallback, useRef, useState } from "react";

export function useTabTotals() {
  const [totals, setTotals] = useState({});
  const settersRef = useRef({});

  /** The setter for one tab — the same function every time it is asked for. */
  const setterFor = useCallback((stateKey) => {
    if (!stateKey) return undefined;
    if (!settersRef.current[stateKey]) {
      settersRef.current[stateKey] = (total) =>
        setTotals((prev) =>
          prev[stateKey] === total ? prev : { ...prev, [stateKey]: total }
        );
    }
    return settersRef.current[stateKey];
  }, []);

  /** A tab's count, or undefined for one that has never reported a count. */
  const totalFor = useCallback((stateKey) => totals[stateKey], [totals]);

  return { setterFor, totalFor };
}

export default useTabTotals;
